import {
  IconAntennaBars4,
  IconBasketDiscount,
  IconUserHeart,
  IconUserPlus,
  IconUsersGroup,
} from '@tabler/icons-react'
import ReactEcharts from 'echarts-for-react'
import { useEffect } from 'react'
import { useTranslation } from 'react-i18next'

import {
  ContentCard,
  Leaderboard,
  MetricCard,
  ScalrCarousel,
} from '@/components/customComponents'
import { TempLoyaltyChart } from '@/components/ui/charts'

import { metricCardColors } from '@/constants/COLOR_PALETTE'
import { toast, useLoyaltyMetrics } from '@/hooks'
import {
  selectPageRefreshTrigger,
  useAppSelector,
  useReduxBaseFilter,
} from '@/store'
import { KpiGoalsModel } from '@/types'
import { dateToString } from '@/utils'
import { DateType } from '@/utils/date'

export default function LoyaltyPage({
  kpiGoals,
  kpiGoalsLoading,
}: {
  kpiGoals: KpiGoalsModel
  kpiGoalsLoading: boolean
}) {
  const { t } = useTranslation()
  const { selectedBranch, date, dateFilterType } = useReduxBaseFilter('loyalty')
  const refreshTrigger = useAppSelector(selectPageRefreshTrigger('loyalty'))

  const {
    // branchData,
    companyData,
    metricCardData,
    personaData,
    personaLeaderboardColumns,
    frequencyChart7DaysData,
    frequencyChart30DaysData,
    churnRateChartData,
    orderCountBracketsChartData,
    loading,
    error: loyaltyMetricsError,
    refetch,
  } = useLoyaltyMetrics(
    dateToString(date, 'from'),
    dateToString(date, 'to'),
    dateFilterType === 'test-range'
      ? 'month-to-date'
      : (dateFilterType as DateType),
    selectedBranch
  )

  // const {
  //   processedData,
  //   loading: regionCardsLoading,
  //   error: regionCardsError,
  //   refetch: regionCardsRefetch,
  // } = useLoyaltyRegionBoxMetrics(
  //   dateToString(date, 'from'),
  //   dateToString(date, 'to'),
  //   dateFilterType as DateType
  // )

  // Refetch data when refresh trigger is triggered
  useEffect(() => {
    if (refreshTrigger) {
      console.log('Refreshing loyalty page data...')
      refetch()
      // regionCardsRefetch()
    }
  }, [refreshTrigger, refetch])

  // Show error toast if there is an error
  useEffect(() => {
    if (loyaltyMetricsError) {
      toast({
        title: t('common.errors.serverError'),
        description: loyaltyMetricsError?.message,
        variant: 'destructive',
      })
    }
  }, [loyaltyMetricsError])

  return (
    <div className="space-y-6">
      <div className="space-y-3 mb-10">
        <ScalrCarousel>
          <MetricCard
            title={t('clarity.loyalty.title')}
            mtdCurrContent={
              metricCardData?.currMetricsMonthly?.loyaltyPercentage
            }
            ytdCurrContent={
              metricCardData?.currMetricsYearly?.loyaltyPercentage
            }
            mtdPrevContent={
              metricCardData?.prevMetricsMonthly?.loyaltyPercentage
            }
            ytdPrevContent={
              metricCardData?.prevMetricsYearly?.loyaltyPercentage
            }
            content={companyData?.loyaltyPercentage?.toFixed(2)}
            type="percentage"
            kpiGoal={kpiGoals?.loyalty?.loyaltyPercentage}
            loading={loading || kpiGoalsLoading}
            icon={<IconUsersGroup color="white" size={20} />}
            color={metricCardColors.blue500}
          />
          <MetricCard
            title={t('clarity.loyalty.newUser')}
            mtdCurrContent={
              metricCardData?.currMetricsMonthly?.newUserPercentage
            }
            ytdCurrContent={
              metricCardData?.currMetricsYearly?.newUserPercentage
            }
            mtdPrevContent={
              metricCardData?.prevMetricsMonthly?.newUserPercentage
            }
            ytdPrevContent={
              metricCardData?.prevMetricsYearly?.newUserPercentage
            }
            content={companyData?.newUserPercentage?.toFixed(2)}
            type="percentage"
            kpiGoal={kpiGoals?.loyalty?.newUserPercentage}
            loading={loading || kpiGoalsLoading}
            icon={<IconUserPlus color="white" size={20} />}
            color={metricCardColors.red500}
          />
          <MetricCard
            title={t('clarity.loyalty.sevenDayFrequency')}
            mtdCurrContent={
              metricCardData?.currMetricsMonthly?.avgFrequency7day
            }
            ytdCurrContent={metricCardData?.currMetricsYearly?.avgFrequency7day}
            mtdPrevContent={
              metricCardData?.prevMetricsMonthly?.avgFrequency7day
            }
            ytdPrevContent={metricCardData?.prevMetricsYearly?.avgFrequency7day}
            content={companyData?.avgFrequency7day?.toFixed(2)}
            type="number"
            kpiGoal={kpiGoals?.loyalty?.frequency}
            loading={loading || kpiGoalsLoading}
            icon={<IconAntennaBars4 color="white" size={20} />}
            color={metricCardColors.green500}
          />
          <MetricCard
            title={t('clarity.loyalty.retentionRate')}
            mtdCurrContent={
              metricCardData?.currMetricsMonthly?.retentionPercentage
            }
            ytdCurrContent={
              metricCardData?.currMetricsYearly?.retentionPercentage
            }
            mtdPrevContent={
              metricCardData?.prevMetricsMonthly?.retentionPercentage
            }
            ytdPrevContent={
              metricCardData?.prevMetricsYearly?.retentionPercentage
            }
            content={companyData?.retentionPercentage?.toFixed(2)}
            type="percentage"
            kpiGoal={kpiGoals?.loyalty?.retentionPercentage}
            loading={loading || kpiGoalsLoading}
            icon={<IconUserHeart color="white" size={20} />}
            color={metricCardColors.orange500}
          />
          <MetricCard
            title={t('clarity.loyalty.discountPenetration')}
            mtdCurrContent={
              metricCardData?.currMetricsMonthly?.discountPenetrationPercentage
            }
            ytdCurrContent={
              metricCardData?.currMetricsYearly?.discountPenetrationPercentage
            }
            mtdPrevContent={
              metricCardData?.prevMetricsMonthly?.discountPenetrationPercentage
            }
            ytdPrevContent={
              metricCardData?.prevMetricsYearly?.discountPenetrationPercentage
            }
            content={companyData?.discountPenetrationPercentage?.toFixed(2)}
            type="percentage"
            kpiGoal={kpiGoals?.loyalty?.discountPenetrationPercentage}
            loading={loading || kpiGoalsLoading}
            icon={<IconBasketDiscount color="white" size={20} />}
            color={metricCardColors.blue500}
          />
        </ScalrCarousel>
      </div>

      <Leaderboard
        title={t('clarity.loyalty.personaLeaderboard')}
        data={personaData}
        columns={personaLeaderboardColumns}
        loading={loading}
        sortBy="orderCount"
        enablePagination
      />

      <div className="grid grid-cols-1 md:grid-cols-12 gap-4">
        <div className="col-span-1 md:col-span-6">
          <ContentCard
            title={t('clarity.loyalty.frequency')}
            loading={!frequencyChart7DaysData || !frequencyChart30DaysData}
            isEmpty={!frequencyChart7DaysData && !frequencyChart30DaysData}
          >
            {frequencyChart7DaysData && frequencyChart30DaysData && (
              <div className="grid grid-cols-2">
                <ReactEcharts option={frequencyChart7DaysData} />
                <ReactEcharts option={frequencyChart30DaysData} />
              </div>
            )}
          </ContentCard>
        </div>
        <div className="col-span-1 md:col-span-3">
          <ContentCard
            title={t('clarity.loyalty.churnRate')}
            infoDescription={t('clarity.loyalty.churnRateDescription')}
            loading={!churnRateChartData}
            isEmpty={!churnRateChartData}
          >
            {churnRateChartData && <ReactEcharts option={churnRateChartData} />}
          </ContentCard>
        </div>
        <div className="col-span-1 md:col-span-3">
          <ContentCard
            title={t('clarity.loyalty.orderCountBrackets')}
            loading={!orderCountBracketsChartData}
            isEmpty={!orderCountBracketsChartData}
          >
            {orderCountBracketsChartData && (
              <ReactEcharts option={orderCountBracketsChartData} />
            )}
          </ContentCard>
        </div>
      </div>

      <TempLoyaltyChart first={false} second={true} />
    </div>
  )
}
